import { useState } from 'react'

import {
  useApproveAction,
  usePlaybooks,
  usePlaybookTransition,
  useResponseActions,
  useRollbackAction,
  type PlaybookRow,
  type ResponseAction,
} from '../api/queries'
import { useTenant } from '../api/tenant'
import { Failure } from '../components/Failure'
import type { Session } from '../api/types'

/**
 * What the platform did to the estate, and what it is allowed to do next.
 *
 * The screen is built around two facts an analyst needs quickly and one they need
 * eventually. Quickly: what is waiting for me, and what can I undo. Eventually: what
 * is this thing permitted to do on its own.
 *
 * Refusals are shown as prominently as successes, and never styled as errors. A
 * refusal is a control working — the host declining to block the management range —
 * and a screen that paints it red teaches people to route around it.
 */

const HUMAN: Record<string, string> = {
  planned: 'Planned (dry run)',
  awaiting_approval: 'Waiting for you',
  approved: 'Approved',
  executed: 'Done',
  failed: 'Failed',
  refused: 'Refused by the host',
  unknown: 'Unknown — no answer',
  expired: 'Approval expired',
  rolled_back: 'Rolled back',
  cancelled: 'Cancelled',
}

// Which statuses read as "somebody should look at this". Deliberately includes
// `unknown`: an action nobody confirmed is the one worth chasing, and it is the one
// most easily mistaken for done.
const NEEDS_ATTENTION = new Set(['awaiting_approval', 'unknown', 'failed'])

function actionSummary(action: ResponseAction): string {
  const verb = action.action.replace(/_/g, ' ')
  if (action.target_field) return `${verb} ${action.target} (from ${action.target_field})`
  return `${verb} ${action.target}`
}

function ttl(seconds: number): string {
  if (seconds >= 3600) return `${Math.round(seconds / 3600)}h`
  if (seconds >= 60) return `${Math.round(seconds / 60)}m`
  return `${seconds}s`
}

export function ResponsePage({
  tenants = [],
  session,
}: {
  tenants?: string[]
  session?: Session
}) {
  // The tenants this account can see, from the session when the caller did not say.
  // A screen that decides what to offer from a role has to be looking at the right
  // membership, and defaulting to none would silently show a responder the read-only
  // view.
  const available = tenants.length > 0 ? tenants : (session?.memberships ?? []).map((m) => m.tenant)
  const [tenant] = useTenant(available)
  const actions = useResponseActions(tenant ? { tenant } : {})
  const playbooks = usePlaybooks(tenant || undefined)
  const approve = useApproveAction(tenant || undefined)
  const rollback = useRollbackAction(tenant || undefined)
  const transition = usePlaybookTransition(tenant || undefined)
  const [undoing, setUndoing] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const role = session?.memberships.find((m) => m.tenant === tenant)?.role ?? 'read_only'
  const mayRespond = role === 'responder' || role === 'admin'
  const mayAdminister = role === 'admin'

  if (actions.isError) return <Failure error={actions.error} onRetry={() => actions.refetch()} />

  const rows = actions.data?.results ?? []
  const waiting = rows.filter((row) => NEEDS_ATTENTION.has(row.status))

  return (
    <section aria-labelledby="response-heading">
      <h2 id="response-heading">Response</h2>

      {waiting.length > 0 && (
        <p role="status" className="panel">
          {waiting.length} action(s) need somebody: approvals waiting, hosts that never
          answered, and failures.
        </p>
      )}

      <section aria-labelledby="actions-heading">
        <h3 id="actions-heading">Actions</h3>
        {actions.isSuccess && rows.length === 0 && (
          <p className="empty">
            Nothing has been done to a host in this tenant. That is the expected state:
            playbooks start in draft and act only after somebody promotes them.
          </p>
        )}

        <ul style={{ listStyle: 'none', padding: 0 }}>
          {rows.map((action) => (
            <li key={action.id} className="panel">
              <strong>{actionSummary(action)}</strong>{' '}
              <span className="mono muted">on {action.hostname}</span>
              <p style={{ margin: 'var(--space-1) 0' }}>
                <span data-status={action.status}>{HUMAN[action.status] ?? action.status}</span>
                {action.dry_run && <span className="muted"> · dry run, nothing was changed</span>}
                {action.ttl_seconds > 0 && (
                  <span className="muted"> · for {ttl(action.ttl_seconds)}</span>
                )}
              </p>
              <p className="muted" style={{ margin: 0 }}>
                {action.playbook_id
                  ? `${action.playbook_id}, step ${action.step_index}`
                  : 'asked for by a person'}{' '}
                · requested by {action.requested_by}
                {action.approved_by && ` · approved by ${action.approved_by}`}
                {action.rolled_back_by && ` · rolled back by ${action.rolled_back_by}`}
              </p>
              {action.detail && (
                <p className="muted" style={{ margin: 'var(--space-1) 0' }}>
                  {action.detail}
                </p>
              )}
              {/* What the host was told to check the target against. Shown because
                  "we killed pid 1837" means nothing without "which we had
                  established was /tmp/.x/miner". */}
              {Object.keys(action.identity ?? {}).length > 0 && (
                <p className="mono muted" style={{ margin: 0 }}>
                  verified against{' '}
                  {Object.entries(action.identity)
                    .map(([key, value]) => `${key}=${value}`)
                    .join(', ')}
                </p>
              )}
              {action.planned_commands?.length > 0 && (
                <details>
                  <summary>What the host ran</summary>
                  <pre className="mono" style={{ overflow: 'auto' }}>
                    {action.planned_commands.map((argv) => argv.join(' ')).join('\n')}
                  </pre>
                </details>
              )}

              {mayRespond && action.status === 'awaiting_approval' && (
                <p>
                  <button
                    type="button"
                    onClick={() => approve.mutate({ id: action.id })}
                    disabled={approve.isPending}
                  >
                    Approve and run
                  </button>{' '}
                  <span className="muted">
                    needs {action.approval_roles.join(' or ')}
                    {action.approval_expires_at &&
                      ` · expires ${new Date(action.approval_expires_at).toLocaleTimeString()}`}
                  </span>
                </p>
              )}

              {mayRespond && action.is_reversible && (
                <div>
                  {undoing === action.id ? (
                    <form
                      onSubmit={(event) => {
                        event.preventDefault()
                        rollback.mutate({ id: action.id, reason })
                        setUndoing(null)
                        setReason('')
                      }}
                    >
                      <label htmlFor={`reason-${action.id}`}>Why are you undoing this?</label>{' '}
                      <input
                        id={`reason-${action.id}`}
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        required
                      />{' '}
                      <button type="submit">Roll back</button>{' '}
                      <button type="button" onClick={() => setUndoing(null)}>
                        Cancel
                      </button>
                    </form>
                  ) : (
                    <button type="button" onClick={() => setUndoing(action.id)}>
                      Roll back
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="playbooks-heading">
        <h3 id="playbooks-heading">Playbooks</h3>
        <p className="muted">
          A playbook does nothing until it has recorded a dry run and somebody has read it.
          There is no path from draft to enabled.
        </p>
        {playbooks.isError && (
          <Failure error={playbooks.error} onRetry={() => playbooks.refetch()} />
        )}
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {(playbooks.data?.results ?? []).map((book: PlaybookRow) => (
            <li key={book.playbook_id} className="panel">
              <strong>{book.playbook_name || book.playbook_id}</strong>{' '}
              <span data-status={book.status}>{book.status.replace('_', ' ')}</span>
              {book.missing && (
                <p role="status">
                  This playbook has a state here but no file on disk. It is not doing what
                  this row says.
                </p>
              )}
              {book.description && <p style={{ margin: 'var(--space-1) 0' }}>{book.description}</p>}
              <ol className="muted">
                {book.steps.map((step, index) => (
                  <li key={index}>
                    <span className="mono">
                      {step.action.replace(/_/g, ' ')}
                      {step.target ? ` ${step.target}` : ' (this host)'}
                    </span>{' '}
                    for {ttl(step.ttl_seconds)}
                    {step.approval_roles.length > 0 &&
                      ` · needs approval from ${step.approval_roles.join(' or ')}`}
                  </li>
                ))}
              </ol>
              <p className="muted" style={{ margin: 0 }}>
                {book.dry_runs} dry run(s) recorded
                {book.enabled_by && ` · enabled by ${book.enabled_by}`}
                {book.disabled_reason && ` · disabled: ${book.disabled_reason}`}
              </p>
              {mayAdminister && !book.missing && (
                <p>
                  {book.status === 'draft' && (
                    <button
                      type="button"
                      onClick={() =>
                        transition.mutate({ id: book.playbook_id, transition: 'dry_run' })
                      }
                    >
                      Start dry run
                    </button>
                  )}{' '}
                  {book.status === 'dry_run' && (
                    <button
                      type="button"
                      onClick={() =>
                        transition.mutate({ id: book.playbook_id, transition: 'enable' })
                      }
                      disabled={book.dry_runs === 0}
                      title={
                        book.dry_runs === 0
                          ? 'Nothing to read yet: this playbook has not matched a finding while in dry run'
                          : undefined
                      }
                    >
                      Enable
                    </button>
                  )}{' '}
                  {book.status !== 'disabled' && book.status !== 'draft' && (
                    <button
                      type="button"
                      onClick={() =>
                        transition.mutate({
                          id: book.playbook_id,
                          transition: 'disable',
                          reason: window.prompt('Why is this being turned off?') ?? '',
                        })
                      }
                    >
                      Disable
                    </button>
                  )}
                </p>
              )}
            </li>
          ))}
        </ul>
      </section>
    </section>
  )
}
